/**
 * RM V3 · RM-F.2 — time-window vehicle availability contract.
 *
 * A vehicle availability record is an INTERVAL, not a day flag: one vehicle may
 * be referenced by different drivers at different times on the same service
 * date (Van 1 · Driver A 08:00–13:00, then Driver B 14:00–19:00).
 *
 * Operational reference ONLY. Nothing here (or in any module importing it)
 * sets `tasks.assigned_driver_id`, writes a task, stop, route or saved plan,
 * triggers reassignment, or feeds optimisation. F0 is untouched: POS remains
 * authoritative before actual route start and the execution boundary after
 * start is unchanged. RM-C stays shadow-only.
 */

export const WINDOW_STATUSES = ['available', 'unavailable', 'reserved', 'in_use'] as const;
export type WindowStatus = (typeof WINDOW_STATUSES)[number];

/** Legacy day-only rows may still carry `limited`; it is read-only now. */
export type StoredWindowStatus = WindowStatus | 'limited';

export const WINDOW_STATUS_LABELS: Record<StoredWindowStatus, string> = {
  available: 'Available',
  unavailable: 'Unavailable',
  reserved: 'Reserved',
  in_use: 'In use',
  limited: 'Limited (legacy)',
};

/**
 * Overlap contract — mirrors the DB exclusion constraint
 * `fleet_vehicle_availability_no_overlap` exactly.
 *
 * Blocking statuses may never overlap each other for the same vehicle.
 * `available` (and legacy `limited`) are envelopes and may overlap anything —
 * they assert capability, not occupancy. Intervals are half-open `[start, end)`
 * so 08:00–13:00 and 13:00–19:00 are adjacent and both accepted.
 */
export const BLOCKING_STATUSES: readonly StoredWindowStatus[] = [
  'unavailable',
  'reserved',
  'in_use',
];

export function isBlockingStatus(status: string): boolean {
  return (BLOCKING_STATUSES as readonly string[]).includes(status);
}

export interface VehicleWindow {
  id: string;
  status: StoredWindowStatus;
  /** ISO-8601 instant. */
  startAt: string;
  /** ISO-8601 instant, strictly after `startAt`. */
  endAt: string;
  driverProfileId: string | null;
  /**
   * RM-F.3 (final) — `max_service_stops` FOR THIS WINDOW. `null` means
   * "Not set": it is never zero, and it is never inherited from the vehicle
   * master (whose capacity columns are legacy and non-authoritative).
   * Informational only — no planning, optimisation or assignment consumes it.
   * See `SERVICE_STOP_DEFINITION` for what counts as one service stop.
   */
  capacityValue: number | null;
  capacityUnit: string | null;

  notes: string | null;
}

export function parseVehicleWindows(raw: unknown): VehicleWindow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((w) => w && w.start_at && w.end_at)
    .map((w: any) => ({
      id: w.id,
      status: w.availability_status as StoredWindowStatus,
      startAt: w.start_at,
      endAt: w.end_at,
      driverProfileId: w.driver_profile_id ?? null,
      // 0 is a real capacity; only a missing value stays unknown.
      capacityValue:
        w.capacity_value === null || w.capacity_value === undefined
          ? null
          : Number(w.capacity_value),
      capacityUnit: w.capacity_unit ?? null,
      notes: w.notes ?? null,
    }))
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
}

/**
 * RM-F.3 (final) — the single supported operational capacity measure for this
 * pilot: `max_service_stops` per vehicle service window.
 *
 * SERVICE STOP DEFINITION (see `SERVICE_STOP_DEFINITION`):
 *  • one distinct planned physical visit — pickup, delivery or operational
 *    stop — counts once, including an internal paired drop-off when it is
 *    physically visited;
 *  • several tasks grouped into one physical stop count as ONE stop;
 *  • the route start depot and the route end depot are NOT service stops —
 *    they affect travel-time / route feasibility only;
 *  • a factory, outlet or customer location counts only when it is an actual
 *    planned work stop, not a depot positioning point.
 */
export const SERVICE_STOP_CAPACITY_UNIT = 'service_stops' as const;

export const SERVICE_STOP_DEFINITION =
  'One service stop = one distinct planned physical visit (pickup, delivery or operational stop), including internal paired drop-offs that are physically visited. Several tasks at the same physical stop count once. Route start and end depots are not service stops.';

/** Capacity measures accepted for NEW window entries (mirrors the DB CHECK). */
export const WINDOW_CAPACITY_UNITS = [SERVICE_STOP_CAPACITY_UNIT] as const;
export type WindowCapacityUnit = (typeof WINDOW_CAPACITY_UNITS)[number];

/**
 * Pre-final RM-F.3 units. Kept ONLY so historical rows stay readable; they are
 * non-authoritative and can no longer be selected.
 */
export const LEGACY_WINDOW_CAPACITY_UNITS = ['orders', 'kg', 'm3', 'pieces'] as const;

export const WINDOW_CAPACITY_UNIT_LABELS: Record<string, string> = {
  service_stops: 'service stops',
  orders: 'orders (legacy)',
  kg: 'kg (legacy)',
  m3: 'm³ (legacy)',
  pieces: 'pieces (legacy)',
};

/**
 * Max service stops for a window. A missing capacity is ALWAYS "Not set" —
 * never 0, never silently taken from the vehicle record.
 */
export function formatWindowCapacity(
  value: number | null | undefined,
  unit: string | null | undefined,
): string {
  if (value === null || value === undefined) return 'Not set';
  const resolved = unit ?? SERVICE_STOP_CAPACITY_UNIT;
  if (resolved === SERVICE_STOP_CAPACITY_UNIT) {
    return `${value} ${value === 1 ? 'service stop' : 'service stops'}`;
  }
  const label = WINDOW_CAPACITY_UNIT_LABELS[resolved] ?? resolved;
  return label ? `${value} ${label}` : String(value);
}



// ---------------------------------------------------------------------------
// Timezone helpers — tenant-local editing over UTC instants.
// ---------------------------------------------------------------------------

/** Offset of `tz` from UTC, in minutes, at the given instant. */
function tzOffsetMinutes(instant: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = Object.fromEntries(dtf.formatToParts(instant).map((p) => [p.type, p.value]));
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) === 24 ? 0 : Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return (asUtc - instant.getTime()) / 60000;
}

/**
 * Converts a tenant-local `yyyy-MM-dd` + `HH:mm` into a UTC ISO instant.
 * Two-pass so DST transitions resolve against the correct offset.
 */
export function zonedToInstant(date: string, time: string, tz: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const naive = Date.UTC(y, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0);
  let guess = new Date(naive - tzOffsetMinutes(new Date(naive), tz) * 60000);
  guess = new Date(naive - tzOffsetMinutes(guess, tz) * 60000);
  return guess.toISOString();
}

/** Renders an instant as tenant-local `HH:mm`. */
export function instantToZonedTime(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Renders an instant as tenant-local `yyyy-MM-dd`. */
export function instantToZonedDate(iso: string, tz: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** `08:00 – 13:00` in tenant-local time, with a day marker when it spans days. */
export function formatWindowRange(window: VehicleWindow, tz: string, serviceDate?: string): string {
  const start = instantToZonedTime(window.startAt, tz);
  const end = instantToZonedTime(window.endAt, tz);
  const endDate = instantToZonedDate(window.endAt, tz);
  const startDate = instantToZonedDate(window.startAt, tz);
  if (serviceDate && startDate !== serviceDate) return `${start} (${startDate}) – ${end}`;
  if (endDate !== startDate) return `${start} – ${end} (${endDate})`;
  return `${start} – ${end}`;
}

// ---------------------------------------------------------------------------
// Validation — mirrors the DB constraints so the UI fails fast and in English.
// ---------------------------------------------------------------------------

export interface WindowInput {
  /** Present when editing; excluded from its own overlap check. */
  id?: string;
  status: string;
  /** tenant-local `yyyy-MM-dd` */
  date: string;
  /** tenant-local `HH:mm` */
  startTime: string;
  /** tenant-local `HH:mm`; `00:00` means midnight of the next day. */
  endTime: string;
  driverProfileId: string | null;
  /** RM-F.3 — empty string / null means "Not set", never 0. */
  capacityValue?: string | number | null;
  capacityUnit?: string | null;
}


export interface WindowValidation {
  ok: boolean;
  errors: Record<string, string>;
  startAt?: string;
  endAt?: string;
}

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function resolveWindowInstants(
  input: WindowInput,
  tz: string,
): { startAt: string; endAt: string } {
  const startAt = zonedToInstant(input.date, input.startTime, tz);
  let endAt = zonedToInstant(input.date, input.endTime, tz);
  // A window that ends at or before it starts is read as ending next day
  // (e.g. a 20:00 – 02:00 night shift). The strict check still applies after.
  if (input.endTime < input.startTime) {
    const next = new Date(`${input.date}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    endAt = zonedToInstant(next.toISOString().slice(0, 10), input.endTime, tz);
  }
  return { startAt, endAt };
}

export function validateWindowInput(
  input: WindowInput,
  existing: VehicleWindow[],
  tz: string,
): WindowValidation {
  const errors: Record<string, string> = {};

  if (!(WINDOW_STATUSES as readonly string[]).includes(input.status)) {
    errors.status = 'Choose an availability status.';
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.date)) errors.date = 'Choose a service date.';
  if (!TIME_RE.test(input.startTime)) errors.startTime = 'Enter a start time as HH:MM.';
  if (!TIME_RE.test(input.endTime)) errors.endTime = 'Enter an end time as HH:MM.';

  // RM-F.3 (final) max_service_stops is optional; when given it must be a
  // whole, non-negative number of service stops. Empty stays "Not set" — it
  // never becomes 0.
  const capRaw = input.capacityValue;
  const capProvided = capRaw !== null && capRaw !== undefined && capRaw !== '';
  if (capProvided) {
    const n = Number(capRaw);
    if (!Number.isFinite(n)) errors.capacityValue = 'Max service stops must be a number.';
    else if (n < 0) errors.capacityValue = 'Max service stops cannot be negative.';
    else if (!Number.isInteger(n)) {
      errors.capacityValue = 'Max service stops must be a whole number of stops.';
    }
    if (!(WINDOW_CAPACITY_UNITS as readonly string[]).includes(input.capacityUnit ?? '')) {
      errors.capacityUnit = 'Capacity for a service window is measured in service stops.';
    }
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };


  const { startAt, endAt } = resolveWindowInstants(input, tz);
  if (new Date(endAt).getTime() <= new Date(startAt).getTime()) {
    errors.endTime = 'The end time must be later than the start time.';
    return { ok: false, errors };
  }

  const clash = findOverlap({ id: input.id, status: input.status, startAt, endAt }, existing);
  if (clash) {
    errors.startTime = overlapMessage(clash, tz);
    return { ok: false, errors, startAt, endAt };
  }

  return { ok: true, errors, startAt, endAt };
}

/**
 * Client-side preflight for the DB exclusion constraint. Returns the first
 * conflicting window, or `null`. Adjacent windows never conflict.
 */
export function findOverlap(
  candidate: { id?: string; status: string; startAt: string; endAt: string },
  existing: VehicleWindow[],
): VehicleWindow | null {
  if (!isBlockingStatus(candidate.status)) return null;
  const s = new Date(candidate.startAt).getTime();
  const e = new Date(candidate.endAt).getTime();
  return (
    existing.find((w) => {
      if (w.id === candidate.id) return false;
      if (!isBlockingStatus(w.status)) return false;
      const ws = new Date(w.startAt).getTime();
      const we = new Date(w.endAt).getTime();
      return s < we && ws < e; // half-open [start, end)
    }) ?? null
  );
}

export function overlapMessage(clash: VehicleWindow, tz: string): string {
  return `This overlaps an existing ${WINDOW_STATUS_LABELS[clash.status].toLowerCase()} window (${formatWindowRange(clash, tz)}). Adjust the times so they do not overlap — windows may touch, for example 08:00–13:00 then 13:00–19:00.`;
}

/** Turns a Postgres error into an operator-readable sentence. */
export function describeWindowError(error: unknown): string {
  const err = error as { code?: string; message?: string } | null;
  const code = err?.code;
  const message = err?.message ?? '';

  if (code === '23P01' || /no_overlap|exclusion constraint/i.test(message)) {
    return 'This time window overlaps another unavailable, reserved or in-use window for the same vehicle. Windows may touch (13:00 then 13:00) but not overlap.';
  }
  if (/capacity_valid/i.test(message)) {
    return 'Max service stops for this time window must be a whole number of zero or more.';
  }

  if (code === '23514' || /interval_valid/i.test(message)) {
    return 'The end time must be later than the start time.';
  }

  if (code === '42501' || /not authorised|does not belong to tenant/i.test(message)) {
    return 'You do not have permission to change vehicle availability for this organisation.';
  }
  return message || 'Request failed.';
}

// ---------------------------------------------------------------------------
// Read-only current / next state (Route Management Overview summary).
// ---------------------------------------------------------------------------

export interface VehicleWindowState {
  current: VehicleWindow | null;
  next: VehicleWindow | null;
  /** True when the date has no timed windows at all. */
  empty: boolean;
}

/**
 * Current = the window containing `at` (blocking windows win over envelopes).
 * Next = the earliest window starting after `at`. Missing data is reported as
 * empty; it is never rendered as "available".
 */
export function resolveVehicleWindowState(
  windows: VehicleWindow[],
  at: Date = new Date(),
): VehicleWindowState {
  if (windows.length === 0) return { current: null, next: null, empty: true };
  const t = at.getTime();

  const active = windows.filter(
    (w) => new Date(w.startAt).getTime() <= t && new Date(w.endAt).getTime() > t,
  );
  const current =
    active.find((w) => isBlockingStatus(w.status)) ?? active[0] ?? null;

  const next =
    windows
      .filter((w) => new Date(w.startAt).getTime() > t)
      .sort((a, b) => a.startAt.localeCompare(b.startAt))[0] ?? null;

  return { current, next, empty: false };
}
